export const buildAssignmentSlotKey = (date, slotOrarioId, postazioneId) =>
  `${date}-${slotOrarioId}-${postazioneId}`;

const normalizeAssignmentDate = (value) => {
  if (value instanceof Date) {
    return value.toISOString().split("T")[0];
  }

  if (typeof value === "string") {
    return value.split("T")[0];
  }

  return value == null ? "" : String(value);
};

const normalizeAssignmentId = (value) => {
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

// La chiave della Map e' un dettaglio della griglia e puo' provenire da un
// render precedente. L'export individua la bozza anche dai campi effettivi
// dell'assegnazione, che sono la fonte affidabile per data, slot e postazione.
export const getPendingAssignmentsForSlot = (
  pendingAssignments,
  date,
  slotOrarioId,
  postazioneId
) => {
  if (!(pendingAssignments instanceof Map)) return [];

  const targetDate = normalizeAssignmentDate(date);
  const targetSlotId = normalizeAssignmentId(slotOrarioId);
  const targetPostazioneId = normalizeAssignmentId(postazioneId);
  const matches = [];

  const keyCandidates = new Set([
    buildAssignmentSlotKey(date, slotOrarioId, postazioneId),
    buildAssignmentSlotKey(
      targetDate,
      targetSlotId ?? slotOrarioId,
      targetPostazioneId ?? postazioneId
    ),
  ]);

  keyCandidates.forEach((key) => {
    const assignments = pendingAssignments.get(key);
    if (Array.isArray(assignments)) {
      matches.push(...assignments);
    }
  });

  pendingAssignments.forEach((assignments, key) => {
    if (!Array.isArray(assignments)) return;
    if (keyCandidates.has(key)) return;

    assignments.forEach((assignment) => {
      if (
        normalizeAssignmentDate(assignment?.data_turno) === targetDate &&
        normalizeAssignmentId(assignment?.slot_orario_id) === targetSlotId &&
        normalizeAssignmentId(assignment?.postazione_id) ===
          targetPostazioneId
      ) {
        matches.push(assignment);
      }
    });
  });

  return mergePersistedAndPendingAssignments([], matches);
};

export const cloneAssignmentDrafts = (pendingAssignments) => {
  const snapshot = new Map();
  if (!(pendingAssignments instanceof Map)) return snapshot;

  pendingAssignments.forEach((assignments, key) => {
    snapshot.set(
      key,
      Array.isArray(assignments)
        ? assignments.map((assignment) => ({ ...assignment }))
        : []
    );
  });

  return snapshot;
};

// La griglia mantiene le modifiche manuali in una bozza locale finché l'utente
// non le salva. Per anteprime ed export la bozza va unita ai dati persistiti,
// senza mai mutare né la bozza né le assegnazioni già caricate dal server.
export const mergePersistedAndPendingAssignments = (
  persistedAssignments = [],
  pendingAssignments = []
) => {
  const assignmentsByVolunteer = new Map();

  [...persistedAssignments, ...pendingAssignments].forEach((assignment) => {
    const volunteerId = parseInt(assignment?.volontario_id, 10);
    if (Number.isNaN(volunteerId) || assignmentsByVolunteer.has(volunteerId)) {
      return;
    }

    assignmentsByVolunteer.set(volunteerId, assignment);
  });

  return Array.from(assignmentsByVolunteer.values());
};

export const formatAssignmentsForExport = (
  assignments = [],
  resolveVolunteer = () => null
) => {
  const names = assignments.map((assignment) => {
    const volunteer =
      assignment?.nome || assignment?.cognome
        ? assignment
        : resolveVolunteer(assignment?.volontario_id);
    const fullName = [volunteer?.nome, volunteer?.cognome]
      .filter(Boolean)
      .join(" ");

    return (
      fullName || `Volontario ${assignment?.volontario_id || "sconosciuto"}`
    );
  });

  return names.join(", ") || "Nessuno";
};
